import { supabase } from './supabase';

const SUBSCRIPTION_QUERY_TIMEOUT_MS = 12000;

async function withTimeout<T>(promiseLike: PromiseLike<T>, timeoutMs: number, operation: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race<T>([
      Promise.resolve(promiseLike),
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`${operation} timed out`));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  stripePriceId: string;
}

export interface UserSubscription {
  id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing' | 'incomplete' | 'incomplete_expired';
  plan_id: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
}

// Define subscription plans
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 5.00,
    interval: 'month',
    stripePriceId: process.env.REACT_APP_STRIPE_PRICE_ID_MONTHLY || 'price_monthly_placeholder',
  },
];

export class StripeService {
  /**
   * Create a Stripe Checkout session for subscription
   */
  async createCheckoutSession(priceId: string): Promise<{ sessionId: string; url: string }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // In local development, package.json proxy forwards /api/* to the deployed Vercel API
      // In production, this is the same origin
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          priceId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      if (!data || !data.sessionId || !data.url) {
        throw new Error('Invalid response from checkout session creation');
      }

      return {
        sessionId: data.sessionId,
        url: data.url,
      };
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      throw new Error(error.message || 'Failed to create checkout session');
    }
  }

  /**
   * Get current user's subscription
   */
  async getUserSubscription(): Promise<UserSubscription | null> {
    try {
      const { data: { user } } = await withTimeout(
        supabase.auth.getUser(),
        SUBSCRIPTION_QUERY_TIMEOUT_MS,
        'Auth user fetch'
      );
      if (!user?.email) return null;

      const { data: publicUser, error: publicUserError } = await withTimeout(
        supabase
          .from('users')
          .select('id')
          .eq('email', user.email)
          .single(),
        SUBSCRIPTION_QUERY_TIMEOUT_MS,
        'Public user lookup'
      );

      if (publicUserError || !publicUser?.id) {
        return null;
      }

      const { data, error } = await withTimeout(
        supabase.rpc('get_user_subscription', {
          p_user_id: publicUser.id,
        }),
        SUBSCRIPTION_QUERY_TIMEOUT_MS,
        'Subscription RPC'
      );

      if (error) throw error;
      if (!data || data.length === 0) return null;

      return data[0] as UserSubscription;
    } catch (error: any) {
      console.error('Error getting user subscription:', error);
      return null;
    }
  }

  /**
   * Check if user has active subscription
   */
  async hasActiveSubscription(): Promise<boolean> {
    const subscription = await this.getUserSubscription();
    return subscription !== null && (subscription.status === 'active' || subscription.status === 'trialing');
  }

  /**
   * Create customer portal session for managing subscription
   */
  async createPortalSession(): Promise<string> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // In local development, package.json proxy forwards /api/* to the deployed Vercel API
      // In production, this is the same origin
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create portal session');
      }

      const data = await response.json();
      if (!data || !data.url) {
        throw new Error('Invalid response from portal session creation');
      }

      return data.url;
    } catch (error: any) {
      console.error('Error creating portal session:', error);
      throw new Error(error.message || 'Failed to create portal session');
    }
  }

  /**
   * Cancel subscription at period end
   * Note: This is handled through the Stripe portal, but we can add a direct API call if needed
   */
  async cancelSubscription(subscriptionId: string): Promise<void> {
    // Subscription cancellation is handled through Stripe portal
    // This method is kept for API consistency but redirects to portal
    throw new Error('Please use the Manage Subscription button to cancel your subscription');
  }

  /**
   * Reactivate canceled subscription
   * Note: This is handled through the Stripe portal, but we can add a direct API call if needed
   */
  async reactivateSubscription(subscriptionId: string): Promise<void> {
    // Subscription reactivation is handled through Stripe portal
    // This method is kept for API consistency but redirects to portal
    throw new Error('Please use the Manage Subscription button to reactivate your subscription');
  }

  /**
   * Get subscription plan details by ID
   */
  getPlanById(planId: string): SubscriptionPlan | undefined {
    return SUBSCRIPTION_PLANS.find(plan => plan.id === planId);
  }

  /**
   * Format price for display
   */
  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  }

  /**
   * Calculate annual savings compared to monthly
   */
  calculateAnnualSavings(): number {
    const monthlyPlan = SUBSCRIPTION_PLANS.find(p => p.interval === 'month');
    const annualPlan = SUBSCRIPTION_PLANS.find(p => p.interval === 'year');
    
    if (!monthlyPlan || !annualPlan) return 0;
    
    const monthlyYearlyCost = monthlyPlan.price * 12;
    return monthlyYearlyCost - annualPlan.price;
  }
}

